import { IClickLogBody } from "@/app/hooks/api_hooks/org/useClickLogAPI";

// The membership extension APIs exchange status as an "active" / "inactive" string,
// while the form works with a boolean toggle.
export const isMembershipExtensionActive = (status: string | boolean): boolean =>
  typeof status === "boolean" ? status : `${status}`.toLowerCase() === "active";

export const toMembershipExtensionStatus = (status: boolean): "active" | "inactive" => (status ? "active" : "inactive");

// The listing/detail payloads are not fully frozen yet, so accept the known id aliases.
export const getMembershipExtensionId = (extension: {
  extension_id?: string;
  id?: string;
  membership_extension_id?: string;
}): string => extension?.extension_id ?? extension?.membership_extension_id ?? extension?.id;

// The Extension Days input is commented out in the setup screen for now, but the
// API still requires the key, so every extension is created with a full year.
export const MEMBERSHIP_EXTENSION_DEFAULT_DAYS = 366;

// Amount accepts digits only, with at most one decimal point and two decimals.
// Anything else typed or pasted is dropped before it reaches the form state.
export const sanitizeAmountInput = (value: string): string => {
  const digitsAndDot = `${value ?? ""}`.replace(/[^\d.]/g, "");
  const [whole, ...decimalParts] = digitsAndDot.split(".");
  const trimmedWhole = whole.replace(/^0+(?=\d)/, "").slice(0, 8);
  // A decimal point cannot lead the value, so drop it until a digit is typed
  if (trimmedWhole === "") return "";
  if (decimalParts.length === 0) return trimmedWhole;
  return `${trimmedWhole}.${decimalParts.join("").slice(0, 2)}`;
};

// Extension days accepts whole numbers only, capped at 3 digits so the 1 - 366
// range check has nothing longer to reject. Re-enable together with the
// Extension Days input in the setup screen.
// export const sanitizeExtensionDaysInput = (value: string): string =>
//   `${value ?? ""}`.replace(/\D/g, "").replace(/^0+/, "").slice(0, 3);

// Click-log screen ids and events for the membership extension flow.
export const MEMBERSHIP_EXTENSION_SCREEN_IDS = {
  LIST: "MEMBERSHIP_EXTENSION_LIST",
  CREATION: "MEMBERSHIP_EXTENSION_CREATION",
} as const;

export const MEMBERSHIP_EXTENSION_CLICK_EVENTS = {
  /* Listing screen */
  EXTENSION_SEARCH_CLICK: "MEMBERSHIP_EXTENSION_SEARCH_CLICK",
  EXTENSION_SETUP_CLICK: "MEMBERSHIP_EXTENSION_SETUP_CLICK",
  EXTENSION_EDIT_CLICK: "MEMBERSHIP_EXTENSION_EDIT_CLICK",
  /* Creation / edit screen */
  CATEGORY_SELECT_CLICK: "MEMBERSHIP_EXTENSION_CATEGORY_SELECT_CLICK",
  SUB_SERVICES_SELECT_CLICK: "MEMBERSHIP_EXTENSION_SUB_SERVICES_SELECT_CLICK",
  CONNECTED_MEMBERSHIPS_SELECT_CLICK: "MEMBERSHIP_EXTENSION_CONNECTED_MEMBERSHIPS_SELECT_CLICK",
  COUPONS_SELECT_CLICK: "MEMBERSHIP_EXTENSION_COUPONS_SELECT_CLICK",
  VOUCHER_FAMILY_SELECT_CLICK: "MEMBERSHIP_EXTENSION_VOUCHER_FAMILY_SELECT_CLICK",
  STATUS_TOGGLE_CLICK: "MEMBERSHIP_EXTENSION_STATUS_TOGGLE_CLICK",
  CART_SEARCH_TOGGLE_CLICK: "MEMBERSHIP_EXTENSION_CART_SEARCH_TOGGLE_CLICK",
  SAVE_CLICK: "MEMBERSHIP_EXTENSION_SAVE_CLICK",
  UPDATE_CLICK: "MEMBERSHIP_EXTENSION_UPDATE_CLICK",
  CANCEL_CLICK: "MEMBERSHIP_EXTENSION_CANCEL_CLICK",
} as const;

export type MembershipExtensionClickEvent =
  (typeof MEMBERSHIP_EXTENSION_CLICK_EVENTS)[keyof typeof MEMBERSHIP_EXTENSION_CLICK_EVENTS];

/**
 * Builds a click-log body for this flow: fills the fields every event shares so
 * the screens only pass the event and whatever is specific to the click.
 */
export const buildMembershipExtensionClickLog = (
  event_type: MembershipExtensionClickEvent,
  scr_id: string,
  identity: Pick<IClickLogBody, "clinic_id" | "clinic_name" | "vms_user_id">,
  extra: Partial<IClickLogBody> = {},
): IClickLogBody => ({
  event_type,
  scr_id,
  timestamp: Date.now(),
  ...identity,
  ...extra,
});
